import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from 'angular2-toaster';
import axios from 'axios';
import {
  ENDPOINTS,
  PLAYLIST_COLORS_BY_PRIORITY,
  USER_ROLES,
} from '../app.config';
import Utils from '../app.utilities';
import { CompanyService } from '../company.service';
import { UserInfoService } from '../shared/user-info.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { CalendarOptionsModel } from './calendar-options.model';
import { ChannelDropdownSettingsModel } from './channel-dropdown-settings.model';
import { CalendarButtonsModel } from './calendar-buttons.model';

const utilities = new Utils();

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css'],
  // Is already provided in ../app.module.ts
  //  but the UserInfoService, in the constructor, needs access to ActivatedRoute.params
  //  https://stackoverflow.com/questions/39977962/angular-2-0-2-activatedroute-is-empty-in-a-service
  providers: [UserInfoService]
})
export class CalendarPreviewComponent implements OnInit {
  router: any;
  events: any;
  route: any;
  companyService: any;
  toaster: any;
  sub: any;
  userRole: any;
  companyId: any;
  companyName: any;
  dropdownSettings: any;
  channelsList: any;
  channelSelected: any;
  navigationEventsBinded = false;
  differentPriorities = [];
  monthNumbers = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
  };
  priorityColors: any;
  Math = Math;
  businesOwnerName : string;
  businessOwnerIdLocalStorage = localStorage.getItem('businessOwnerId');
  daysLabel: string;
  calendarOptionsModel: CalendarOptionsModel;
  channelDropdownSettings: ChannelDropdownSettingsModel;

  constructor(
    route: ActivatedRoute,
    companyService: CompanyService,
    toaster: ToasterService,
    router: Router,
    private userInfoService: UserInfoService,
    private translate: TranslateService
  ) {
    this.router = router;
    this.route = route;
    this.companyService = companyService;
    this.toaster = toaster;
  }

  ngOnInit() {
    this.events = [];
    this.sub = this.route.params.subscribe(params => {
      this.companyId = params.companyId;
      this.companyService.setCompanyId(this.companyId);
      this.getUserInfo();
      this.getChannels();
    });
    this.channelDropdownSettings = new ChannelDropdownSettingsModel();

    this.calendarOptionsModel = new CalendarOptionsModel();
    this.calendarOptionsModel.buttonText = new CalendarButtonsModel();
    this.calendarOptionsModel.buttonText.today = 'Danes';
    this.calendarOptionsModel.firstDay = 1;
    this.calendarOptionsModel.displayEventTime = true;
    this.calendarOptionsModel.dayNamesShort = ['Ned', 'Pon', 'Tor', 'Sre', 'Čet', 'Pet', 'Sob'];
    this.calendarOptionsModel.monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];
    this.calendarOptionsModel.eventRender = (event, element) => {
      for (const item of this.priorityColors) {
        if (item.priority === event.priority) {
          element[0].style.background = item.color;
          break;
        }
      }
    };

    // Translations
    this.translate.stream('SHARED.SelectBox.select_channels').subscribe((res: string) => {
      this.channelDropdownSettings = {
        singleSelection: false,
        enableCheckAll: false,
        text: res,
        selectAllText: 'Select All',
        unSelectAllText: 'Remove All',
        enableSearchFilter: false,
        classes: 'item-separation c-token',
      };
    });
    this.translate.stream('SHARED.Calendar.days').subscribe((res: string) => {
      this.calendarOptionsModel.buttonText.today = res;
      this.daysLabel = res;
    });
    this.translate.stream('SHARED.Calendar.Days.Sun').subscribe((res: string) => {
      this.calendarOptionsModel.dayNamesShort[0] = res;
    });
    this.translate.stream('SHARED.Calendar.Days.Mon').subscribe((res: string) => {
      this.calendarOptionsModel.dayNamesShort[1] = res;
    });
    this.translate.stream('SHARED.Calendar.Days.Tue').subscribe((res: string) => {
      this.calendarOptionsModel.dayNamesShort[2] = res;
    });
    this.translate.stream('SHARED.Calendar.Days.Wed').subscribe((res: string) => {
      this.calendarOptionsModel.dayNamesShort[3] = res;
    });
    this.translate.stream('SHARED.Calendar.Days.Thu').subscribe((res: string) => {
      this.calendarOptionsModel.dayNamesShort[4] = res;
    });
    this.translate.stream('SHARED.Calendar.Days.Fri').subscribe((res: string) => {
      this.calendarOptionsModel.dayNamesShort[5] = res;
    });
    this.translate.stream('SHARED.Calendar.Days.Sat').subscribe((res: string) => {
      this.calendarOptionsModel.dayNamesShort[6] = res;
    });
    this.translate.stream('SHARED.Calendar.Months.May').subscribe((res: string) => {
      this.calendarOptionsModel.monthNamesShort[4] = res;
    });
    this.translate.stream('SHARED.Calendar.Months.Aug').subscribe((res: string) => {
      this.calendarOptionsModel.monthNamesShort[7] = res;
    });
    this.translate.stream('SHARED.Calendar.Months.Oct').subscribe((res: string) => {
      this.calendarOptionsModel.monthNamesShort[9] = res;
    });
  }

  generatePriorityColors() {
    this.priorityColors = [];
    this.differentPriorities.sort((a, b) => {
      return a - b;
    });
    const numOfPriorityColors = 11;
    const numOfDifferentPriorities = this.differentPriorities.length;
    const colorStep = this.Math.round(
      numOfPriorityColors / numOfDifferentPriorities,
    );
    let currentStep = 0;
    if (numOfDifferentPriorities === 0) {
      return;
    }
    if (numOfDifferentPriorities === 1) {
      this.priorityColors.push({
        priority: this.differentPriorities[0],
        color: PLAYLIST_COLORS_BY_PRIORITY['10'],
      });
    } else if (numOfDifferentPriorities === 2) {
      this.priorityColors.push({
        priority: this.differentPriorities[0],
        color: PLAYLIST_COLORS_BY_PRIORITY['0'],
      });
      this.priorityColors.push({
        priority: this.differentPriorities[1],
        color: PLAYLIST_COLORS_BY_PRIORITY['10'],
      });
    } else {
      for (const priority of this.differentPriorities) {
        this.priorityColors.push({
          priority,
          color: PLAYLIST_COLORS_BY_PRIORITY[currentStep.toString()],
        });
        currentStep += colorStep;
      }
    }
  }

  generateChannelsList(channels) {
    const channelsList = [];
    for (const item of channels) {
      channelsList.push({
        id: item.id,
        itemName: item.name,
      });
    }
    return channelsList;
  }

  getChannels() {
    const _self = this;
    axios
      .get(ENDPOINTS.GET_CHANNELS.replace(':companyId', _self.companyId))
      .then(response => {
        _self.channelsList = _self.generateChannelsList(response.data);
      });
  }

  generateCalendarEvents(data) {
    this.differentPriorities = [];
    Object.keys(data).forEach(day => {
      for (const playlist of data[day]) {
        const dateTime = day.split(' ');
        const date = dateTime[0].split('-');
        const configuredTime = date[2] + '-' + date[1] + '-' + date[0];
        const startTime = playlist.valid_from.split('T');
        const endTime = playlist.valid_to.split('T');
        this.events.push({
          id: playlist.id,
          start: configuredTime + 'T' + startTime[1],
          end: endTime[1] === '00:00:00.000Z'
            ? configuredTime + 'T' + '23:59:00.000Z'
            : configuredTime + 'T' + endTime[1],
          title: playlist.name,
          priority: playlist.priority,
        });
        if (!this.differentPriorities.includes(playlist.priority)) {
          this.differentPriorities.push(playlist.priority);
        }
      }
    });
  }

  initCalendarData(data) {
    this.generateCalendarEvents(data);
    this.generatePriorityColors();
    this.bindNavigationEvents();
  }

  generateCurrentDate() {
    const date = document.getElementById('fc').innerText.split(this.daysLabel)[0];
    const separatedDate = date.split(' ');
    const fromRange = [];
    const toRange = [];
    let toggle = false;
    for (const item of separatedDate) {
      if (item === '–') {
        toggle = true;
        continue;
      }
      if (item === ',') {
        continue;
      }
      if (!toggle) {
        fromRange.push(item);
      } else {
        toRange.push(item);
      }
    }
    const fromMonth = this.monthNumbers[fromRange[0]];
    const fromDay = fromRange[1].replace(',', '');

    const toMonth = this.monthNumbers[toRange[0]] || fromMonth;
    const toDay = !this.monthNumbers[toRange[0]]
      ? toRange[0].replace(',', '')
      : toRange[1].replace(',', '');

    const toYear = toRange[toRange.length - 1];
    const fromYear = fromRange[2] || toYear;

    return {
      from: {
        fromDay,
        fromMonth,
        fromYear,
      },
      to: {
        toDay,
        toMonth,
        toYear,
      },
    };
  }

  bindNavigationEvents() {
    if (!this.navigationEventsBinded) {
      this.navigationEventsBinded = true;
      setTimeout(() => {
        const todayBtn = document.getElementsByClassName('fc-today-button')[0];
        todayBtn.addEventListener('click', () => {
          const generatedDate = this.generateCurrentDate();
          this.loadCalendar(generatedDate);
        });

        const prevBtn = document.getElementsByClassName('fc-prev-button')[0];
        prevBtn.addEventListener('click', () => {
          const generatedDate = this.generateCurrentDate();
          this.loadCalendar(generatedDate);
        });

        const nextBtn = document.getElementsByClassName('fc-next-button')[0];
        nextBtn.addEventListener('click', () => {
          const generatedDate = this.generateCurrentDate();
          this.loadCalendar(generatedDate);
        });
      }, 1000);
    }
  }

  loadCalendar(params?) {
    const _self = this;
    _self.events = [];
    if (_self.channelSelected) {
      let queryParams = '';
      if (params) {
        const from = params.from;
        const to = params.to;
        queryParams +=
          '?view=week&q=' +
          from.fromDay +
          '-' +
          from.fromMonth +
          '-' +
          from.fromYear +
          '|' +
          to.toDay +
          '-' +
          to.toMonth +
          '-' +
          to.toYear;
      }
      axios
        .get(
          ENDPOINTS.CALENDAR
            .replace(':companyId', _self.companyId)
            .replace(':channelId', _self.channelSelected[0].id) + queryParams,
        )
        .then(response => {
          _self.initCalendarData(response.data);
        });
    }
  }

  onItemSelect() {
    const _self = this;
    setTimeout(() => {
      const generatedDate = this.generateCurrentDate();
      _self.loadCalendar(generatedDate);
    }, 300);
  }

  getUserInfo() {
    const _self = this;
    this.userInfoService.getInformations()
      .subscribe(userInfo => {
        _self.userRole         = userInfo.userRole;
        _self.companyName      = userInfo.companyName;
        _self.businesOwnerName = userInfo.businesOwnerName;
      });
  }

  getCompanyInfo() {

  }

  playlistClick(event) {
    window.open(
      '/slideshow/' + event.calEvent.id + '/company/' + this.companyId,
      '_blank',
    );
  }
}



// WEBPACK FOOTER //
// /Users/mihagresak/Documents/git-projects/vintegras-angular-new-clean/src/app/calendar/calendar.component.ts